import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState, AppThunk } from '../../app/store';
import WebsiteOptions from '../../app/dto/websiteOption';
import RetrieveFormDto from '../../app/dto/retrieveDataDto';
import EmployerDto, { EmployerWithHighWaterMark } from '../../app/dto/employerDto';
import DocumentTypeDto from '../../app/dto/documentTypeDto';
import _ from 'lodash';
import RetrieveTranslations from '../../app/dto/translations/retrieveTranslations';
import TranslationDataDto from '../../app/dto/translationDataDto';
import mapToRetrieveTranslations from '../../app/mappers/mapToRetrieveTranslations';
import RetrieveOperationParametersDto from '../../app/dto/operations/RetrieveOperationParametersDto';
import { httpclient as Http } from 'typescript-http-client';
import ExecuteOperationDto from '../../app/dto/operations/ExecuteOperationDto';
import ParameterDto from '../../app/dto/parameterDto';

interface RetrieveFormState extends RetrieveFormDto {
  websiteOptions: Array<WebsiteOptions>;
  employersOptions: Array<EmployerDto>;
  documentTypeOptions: Array<DocumentTypeDto>;
  parameters: Array<ParameterDto>;
  translations: RetrieveTranslations;
  isSending: boolean;
  isError: boolean;
  errorMessage: string;
  isSuccess: boolean;
}

const websiteOptions: Array<WebsiteOptions> = [{ 'name': 'Test site' }, { 'name': 'Live site' }];
const documentTypeOptions: Array<DocumentTypeDto> = [];
const employersOptions: Array<EmployerDto> = [];
const parameters: Array<ParameterDto> = [];

const initialState: RetrieveFormState = {
  userId: '',
  userPassword: '',
  website: websiteOptions[0].name,
  documentType: '',
  employers: [],
  websiteOptions: websiteOptions,
  employersOptions: employersOptions,
  documentTypeOptions: documentTypeOptions,
  parameters: parameters,
  translations: mapToRetrieveTranslations([]),
  isSending: false,
  isError: false,
  errorMessage: '',
  isSuccess: false
};

export const slice = createSlice({
  name: 'retrieve',
  initialState,
  reducers: {
    setUserId: (state, action: PayloadAction<string>) => {
      state.userId = action.payload;
    },
    setPassword: (state, action: PayloadAction<string>) => {
      state.userPassword = action.payload;
    },
    setWebsite: (state, action: PayloadAction<string>) => {
      state.website = action.payload;
    },
    setDocumentType: (state, action: PayloadAction<string>) => {
      state.documentType = action.payload;
    },
    setDocumentTypeOptions: (state, action: PayloadAction<Array<DocumentTypeDto>>) => {
      state.documentTypeOptions = action.payload;
    },
    setEmployersOptions: (state, action: PayloadAction<Array<EmployerDto>>) => {
      state.employersOptions = action.payload;
    },
    setParameters: (state, action: PayloadAction<Array<ParameterDto>>) => {
      state.parameters = action.payload;
    },
    checkEmployers: (state, action: PayloadAction<Array<EmployerWithHighWaterMark>>) => {
      state.employers = action.payload;
    },
    setTranslations: (state, action: PayloadAction<Array<TranslationDataDto>>) => {
      state.translations = mapToRetrieveTranslations(action.payload);
    },
    beginSending: (state) => {
      state.isSending = true;
    },
    finishSending: (state) => {
      state.isSending = false;
    },
    setError: (state, action: PayloadAction<boolean>) => {
      state.isError = action.payload;
      if (!action.payload) state.errorMessage = '';
    },
    setErrorMessage: (state, action: PayloadAction<string>) => {
      state.errorMessage = action.payload;
    },
    setSuccess: (state, action: PayloadAction<boolean>) => {
      state.isSuccess = action.payload;
    }
  },
});

export interface RetrieveParameters {
  OperationId: string;
  User: string;
  Password: string;
  WebSite: string;
  DocumentType: string;
  Employers: EmployerWithHighWaterMark[];
  Timeout: number;
}

export function sendAsync(apiClient: Http.HttpClient, parameters: RetrieveParameters): AppThunk {
  return async (dispatch) => {
    
    await dispatch(beginSending());

    try {

      const setOperationParameters = new Http.Request(`operations/${parameters.OperationId}/parameters`,
        {
          method: 'PUT',
          timeout: parameters.Timeout,
          body: {
            IsTestSite: (parameters.WebSite === 'Test site').toString(),
            DocumentType: parameters.DocumentType,
            Employers: parameters.Employers.map(emp => emp.name).toString(),
            EmployersWithHighWaterMark: JSON.stringify(parameters.Employers)
          } as RetrieveOperationParametersDto
        });

      await apiClient?.execute<void>(setOperationParameters);

      const executeOperation = new Http.Request(`operations/${parameters.OperationId}/executions`,
        {
          method: 'POST',
          timeout: parameters.Timeout,
          body: {
            User: parameters.User,
            Password: parameters.Password
          } as ExecuteOperationDto
        });

      await apiClient?.execute<void>(executeOperation);

    } 
    catch(error){
      const message = error instanceof Error ? error.message : 'An unexpected error occurred';
      dispatch(setErrorMessage(message));
      dispatch(setError(true));
      dispatch(finishSending());
    }
  };
}

export const { setUserId, setPassword, setWebsite, setDocumentType, setDocumentTypeOptions,
  setEmployersOptions, setParameters, checkEmployers, setTranslations, beginSending, finishSending, setError, setErrorMessage, setSuccess } = slice.actions;

export const isValid = (state: RootState) => state.retrieveForm.userId && state.retrieveForm.userPassword
  &&_.size(state.retrieveForm.employers) > 0 && state.retrieveForm.website && state.retrieveForm.documentType
  && state.retrieveForm.employers.every(e => e.sequenceNo != "" && !isNaN(Number(e.sequenceNo)) && Number(e.sequenceNo) >= 0);

export default slice.reducer;
